/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { ICloneCartData } from './clone-cart.data';
import { ICloneCartProps } from './clone-cart.props.autogenerated';

import _, { noop } from 'lodash';

import { IActionContext } from '@msdyn365-commerce/core-internal';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { readAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { getByIdsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

import { Modal, ModalHeader } from '@msdyn365-commerce-modules/utilities';

export interface ICloneCartViewProps extends ICloneCartProps<ICloneCartData> {
    products: SimpleProduct[];
    modal: React.ReactNode;
}
export interface ICloneCartState {
    products: SimpleProduct[];
    modalOpen: boolean;
}

/**
 *
 * CloneCart component
 * @extends {React.PureComponent<ICloneCartProps<ICloneCartData>>}
 */
export default class CloneCart extends React.PureComponent<ICloneCartProps<ICloneCartData>, ICloneCartState> {
    constructor(props: ICloneCartProps<ICloneCartData>) {
        super(props);
        this.state = {
            products: [],
            modalOpen: false
        };
    }
    private oun: string = this.props.context.actionContext.requestContext.apiSettings.oun;
    private channelId: number = this.props.context.actionContext.requestContext.apiSettings.channelId;

    public componentDidMount(): void {
        if (this.props.data.cart && this.props.data.cart?.result && this.props.context.request.cookies.isConsentGiven()) {
            // force async/await
            void (async () => {
                try {
                    void this._cloneCart();
                } catch (err) {
                    noop;
                }
            })();
        }
    }

    public render(): JSX.Element | null {
        if (this.state.products && this.state.products.length > 0) {
            const viewProps: ICloneCartViewProps = { ...this.props, products: this.state.products, modal: this._renderModal() };
            return this.props.renderView(viewProps);
        } else {
            return null;
        }
    }

    private async _cloneCart() {
        const destinationCart = this.props.data.cart.result?.cart;

        const lastCartId: string = this.props.context.request.cookies.get<string>('lastCartId').value || '';
        const lastOun: string = this.props.context.request.cookies.get<string>('lastOun').value || '';
        const lastChannelId: number = Number.parseInt(this.props.context.request.cookies.get<string>('lastChannelId').value || '0', 10);
        if (lastCartId === '' || lastOun === '' || lastChannelId === 0) {
            // set cookies when none found
            this.props.context.request.cookies.set<number>('lastChannelId', this.channelId);
            this.props.context.request.cookies.set<string>('lastOun', this.oun);
            this.props.context.request.cookies.set<string>('lastCartId', destinationCart?.Id!);
        } else if (lastOun !== this.oun && lastChannelId !== this.channelId && lastCartId !== destinationCart?.Id) {
            // Set up previous website context
            const lastCtx = _.cloneDeep(this.props.context.actionContext);
            lastCtx.requestContext.apiSettings.channelId = lastChannelId;
            lastCtx.requestContext.apiSettings.oun = lastOun;

            // retrieve previous website cart
            const sourceCart = await this._readAsync(lastCtx, lastCartId);

            // set cookies to prevent additional runs
            this.props.context.request.cookies.set<number>('lastChannelId', this.channelId);
            this.props.context.request.cookies.set<string>('lastOun', this.oun);
            this.props.context.request.cookies.set<string>('lastCartId', destinationCart?.Id!);

            if (sourceCart.CartLines && sourceCart.CartLines?.length > 0) {
                // retrieve products from previous cart
                const productIdSet: Set<number> = new Set<number>();
                sourceCart.CartLines.forEach(cl => productIdSet.add(cl.ProductId!));
                const products = await this._getByIdsAsync(lastCtx, lastChannelId, Array.from(productIdSet));

                const errors = [];

                // add products to cart individually to catch failed validation
                for await (const cartLine of sourceCart.CartLines) {
                    const productId = cartLine.ProductId;
                    const cartLineNumber = cartLine.LineNumber;
                    if (sourceCart.CartLines?.findIndex(cl => cl.ProductId === productId && cl.LineNumber === cartLineNumber) === -1) {
                        // get product info for current cart line
                        const product = products.find(p => p.RecordId === cartLine.ProductId)!;

                        // add product to cart.  Cannot add multiple lines as a product with failed validation will fail entire add to cart
                        const result = await this._addProductToCart({
                            product: product,
                            count: cartLine.Quantity
                        });

                        // keep track of products that failed to integrate
                        if (result.status === 'FAILED') {
                            errors.push(product);
                        }
                    }
                }

                this.setState({ products: errors, modalOpen: !!errors.length });
            }
        }
    }

    /**
     * Method to retrieve cart.
     * @param ctx -Context of the cart (oun and channelId dictate the website instance).
     * @param cartId -Id of the cart.
     * @returns Cart object.
     */
    private async _readAsync(ctx: IActionContext, cartId: string) {
        return readAsync({ callerContext: ctx }, cartId);
    }

    /**
     * Method to retrieve product info.
     * @param ctx -Context of the cart (oun and channelId dictate the website instance).
     * @param channelId -Channel Id of the website instance.
     * @param products -Ids of the products to retrieve.
     * @returns SimpleProduct array.
     */
    private async _getByIdsAsync(ctx: IActionContext, channelId: number, products: number[]) {
        return getByIdsAsync({ callerContext: ctx }, channelId, products);
    }

    /**
     * Method to add a single product to cart.
     * @param input -products to add to cart, including SimpleProduct and count(quantity)
     * @returns Cart object.
     */
    private async _addProductToCart(input: any) {
        return (await this.props.data.cart).addProductToCart(input);
    }

    private _renderModal() {
        return (
            <Modal isOpen={this.state.modalOpen} toggle={() => this.setState({ modalOpen: false })}>
                <ModalHeader>
                    <div className='msc-cart-line__error-message msc-alert__header'>
                        <span className='msi-exclamation-triangle' />
                        <span>{this.props.resources.resourceKey}</span>
                    </div>
                </ModalHeader>
                {this.state.products.map((p, index) => {
                    return <li key={index}>{p.Name}</li>;
                })}
            </Modal>
        );
    }
}
